import { InteractiveObject } from './InteractiveObject';
import { DisplayObject, HIT_SKIP_INVISIBLE, MousePick, PICK_MISS, RenderKind } from './DisplayObject';

export type ButtonState = 'up' | 'over' | 'down';

/**
 * flash.display.SimpleButton. State objects are pseudo-children (their `parent` is the button);
 * only the current state is rendered / contributes to bounds; hit testing uses `hitTestState`
 * (and the current state's interactive content). State switching follows Ruffle's AVM2 button:
 * RollOver -> over, RollOut -> up, Press -> down, Release -> over, ReleaseOutside -> up,
 * DragOut -> over, DragOver -> down.
 */
export class SimpleButton extends InteractiveObject {
  /** @internal */ $upState: DisplayObject | null;
  /** @internal */ $overState: DisplayObject | null;
  /** @internal */ $downState: DisplayObject | null;
  /** @internal */ $hitTestState: DisplayObject | null;
  /** @internal */ $state: ButtonState;
  /** @internal */ $enabled: boolean;
  /** @internal */ $useHandCursor: boolean;
  /** @internal */ $trackAsMenu: boolean;
  /** @internal */ $soundTransform: any;

  constructor(upState: DisplayObject | null = null, overState: DisplayObject | null = null, downState: DisplayObject | null = null, hitTestState: DisplayObject | null = null) {
    super();
    this.$upState = null;
    this.$overState = null;
    this.$downState = null;
    this.$hitTestState = null;
    this.$state = 'up';
    this.$enabled = true;
    this.$useHandCursor = true;
    this.$trackAsMenu = false;
    this.$soundTransform = null;
    this.upState = upState;
    this.overState = overState;
    this.downState = downState;
    this.hitTestState = hitTestState;
  }

  /** @internal */ get $isButton(): boolean {
    return true;
  }
  /** @internal */ get $renderKind(): RenderKind {
    return 'button';
  }

  private setStateObj(key: '$upState' | '$overState' | '$downState' | '$hitTestState', v: DisplayObject | null): void {
    const old = this[key];
    if (old && old.$parent === (this as any)) {
      const still = [this.$upState, this.$overState, this.$downState, this.$hitTestState].filter((s, i) => s === old && ['$upState', '$overState', '$downState', '$hitTestState'][i] !== key);
      if (!still.length) old.$parent = null;
    }
    this[key] = v ?? null;
    if (v) v.$parent = this as any;
    this.$invalidate();
  }

  get upState(): DisplayObject | null {
    return this.$upState;
  }
  set upState(v: DisplayObject | null) {
    this.setStateObj('$upState', v);
  }
  get overState(): DisplayObject | null {
    return this.$overState;
  }
  set overState(v: DisplayObject | null) {
    this.setStateObj('$overState', v);
  }
  get downState(): DisplayObject | null {
    return this.$downState;
  }
  set downState(v: DisplayObject | null) {
    this.setStateObj('$downState', v);
  }
  get hitTestState(): DisplayObject | null {
    return this.$hitTestState;
  }
  set hitTestState(v: DisplayObject | null) {
    this.setStateObj('$hitTestState', v);
  }
  get enabled(): boolean {
    return this.$enabled;
  }
  set enabled(v: boolean) {
    this.$enabled = !!v;
  }
  get useHandCursor(): boolean {
    return this.$useHandCursor;
  }
  set useHandCursor(v: boolean) {
    this.$useHandCursor = !!v;
  }
  get trackAsMenu(): boolean {
    return this.$trackAsMenu;
  }
  set trackAsMenu(v: boolean) {
    this.$trackAsMenu = !!v;
  }
  get soundTransform(): any {
    return this.$soundTransform;
  }
  set soundTransform(v: any) {
    this.$soundTransform = v;
  }

  /** Current visual state name ('up' | 'over' | 'down'). */
  get $currentState(): ButtonState {
    return this.$state;
  }

  /** Renderer seam: the state object currently displayed. */
  $currentStateObject(): DisplayObject | null {
    return this.$state === 'over' ? this.$overState : this.$state === 'down' ? this.$downState : this.$upState;
  }

  /** @internal */ $setButtonState(s: ButtonState): void {
    if (this.$state !== s) {
      this.$state = s;
      this.$invalidate();
    }
  }

  $lifecycleChildren(): DisplayObject[] | null {
    const out: DisplayObject[] = [];
    for (const s of [this.$upState, this.$overState, this.$downState, this.$hitTestState]) if (s && !out.includes(s)) out.push(s);
    return out;
  }

  $boundsChildren(): DisplayObject[] | null {
    const s = this.$currentStateObject();
    return s ? [s] : null;
  }

  $enterFrame(): void {
    const kids = this.$lifecycleChildren();
    if (kids) for (let i = kids.length - 1; i >= 0; i--) kids[i].$enterFrame();
  }

  $hitTestShape(gx: number, gy: number, options: number): boolean {
    if (options & HIT_SKIP_INVISIBLE && !this.$visible) return false;
    const s = this.$currentStateObject();
    return !!s && s.$hitTestShape(gx, gy, options);
  }

  $mousePick(gx: number, gy: number): MousePick {
    if (!this.$visible || !this.$mouseEnabled) return PICK_MISS;
    const s = this.$currentStateObject();
    if (s && s.$isInteractive) {
      const r = s.$mousePick(gx, gy);
      if (r.kind !== 'miss') return { kind: 'hit', target: this };
    }
    const h = this.$hitTestState;
    if (h && h.$hitTestShape(gx, gy, 0)) return { kind: 'hit', target: this };
    return PICK_MISS;
  }
}
